import {
  useLocation,
  useNavigate,
  type NavigateFunction,
} from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect } from "react";
import routes from "./config";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  useEffect(() => {
    if (location.hash) {
      const animationFrame = window.requestAnimationFrame(() => {
        document
          .getElementById(decodeURIComponent(location.hash.slice(1)))
          ?.scrollIntoView({ behavior: "auto", block: "start" });
      });

      return () => window.cancelAnimationFrame(animationFrame);
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const titles: Record<string, string> = {
      "/": "African Development & Shared Prosperity",
      "/about": "About Harambee",
      "/services": "Our Work",
      "/trading-academy": "Trading Academy | Register Interest",
      "/advisory": "Enterprise & Advisory",
      "/partnerships": "Partnerships",
      "/insights": "Insights & Resources",
      "/contact": "Contact",
      "/leadership": "Our Leadership",
      "/admin": "Employee Portal",
    };
    const title = `${titles[location.pathname] || "Page Not Found"} | Harambee Global Ventures`;
    const descriptions: Record<string, string> = {
      "/": "Connecting African communities and the global diaspora through education, enterprise, and strategic partnerships for shared prosperity.",
      "/about":
        "Learn about Harambee Global Ventures and our commitment to African development, local ownership, and shared opportunity.",
      "/services":
        "Explore Harambee’s education, enterprise, advisory, and partnership priorities, with clear information on service availability.",
      "/trading-academy":
        "Register interest in Harambee’s planned Trading Academy and learn about proposed learning themes and enrollment information.",
      "/advisory":
        "Start a conversation about enterprise, investor readiness, or cross-border collaboration with Harambee Global Ventures.",
      "/partnerships":
        "Explore education, enterprise, and diaspora partnerships grounded in local priorities and African development.",
      "/insights":
        "Practical questions to help prepare for learning, enterprise, and partnership conversations with Harambee.",
      "/contact":
        "Contact Harambee Global Ventures about education, advisory, or partnerships on the continent and across the diaspora.",
      "/leadership":
        "Meet the people guiding Harambee Global Ventures and its pan-African development mission.",
    };
    const description =
      descriptions[location.pathname] || "Harambee Global Ventures.";
    document.title = title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", description);
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute("content", description);
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute("content", title);
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute(
        "href",
        `https://harambeeglobal.com${location.pathname === "/" ? "/" : location.pathname}`,
      );
  }, [location.pathname]);

  return element;
}
